import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { API_URL } from "config";
import FlagLoader from "layouts/flagloader";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const AddBranch = () => {
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [branchName, setBranchName] = useState("");
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [loading, setLoading] = useState(true); // Start with loading true for page load
  const [submitLoading, setSubmitLoading] = useState(false); // separate loading for submit
  const navigate = useNavigate();
  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );
  const hasBranchPermission = (...requiredPermissions) => {
    if (grantedPermissions.includes("access.system.full_access")) return true;
    return requiredPermissions.flat().filter(Boolean).some((permission) => grantedPermissions.includes(permission));
  };
  const canViewPage = hasBranchPermission("branch.pages.create.view");
  const canSubmit =
    hasBranchPermission("branch.actions.create") &&
    hasBranchPermission("branch.actions.submit_create");
  const canOpenList =
    hasBranchPermission("branch.pages.list.view") &&
    hasBranchPermission("branch.actions.open_list");
  const canOpenTrashed =
    hasBranchPermission("branch.pages.trashed.view") &&
    hasBranchPermission("branch.actions.open_trashed");
  const canViewField = (field) => hasBranchPermission(`branch.fields.${field}.view`);

  // Simulate loading on page load (replace with real fetch if needed)
  useEffect(() => {
    // For example, fetch some initial data if required
    // setLoading(false) after data ready
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800); // 0.8 second loading simulation

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setSnackbar({ open: true, message: "You do not have permission to create branches", color: "error" });
      return;
    }

    if (!branchName || !address) {
      setSnackbar({ open: true, message: "Please fill all fields", color: "error" });
      return;
    }

    setSubmitLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/branches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: branchName,
          address,
          status,
        }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Error creating branch");

      setSnackbar({ open: true, message: data.message || "Branch added successfully!", color: "success" });
      setTimeout(() => navigate("/branches"), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Something went wrong", color: "error" });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!isAuthReady || loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          {/* <CircularProgress size={60} color="info" /> */}
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Add Branch
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to add branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Branch"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
      />
      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Add Branch
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenList && (
              <SoftButton
                color="info"
                onClick={() => navigate("/branches")}
                startIcon={<Icon>list</Icon>}
              >
                Branch List
              </SoftButton>
            )}
            {canOpenTrashed && (
              <SoftButton
                color="dark"
                variant="outlined"
                onClick={() => navigate("/branches/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trashed List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <form onSubmit={handleSubmit}>
          <SoftBox display="flex" gap={5} flexWrap="wrap" sx={{ flexDirection: { xs: "column", sm: "row" } }} >
            {canViewField("name") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftInput
                placeholder="Branch Name"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                required
                size="small"
              />
            </SoftBox>
            )}
          </SoftBox>
          {canViewField("address") && (
          <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
            <SoftInput
              size="small"
              placeholder="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
            />
          </SoftBox>
          )}
          {canViewField("status") && (
          <SoftBox display="flex" alignItems="center" mb={2}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <SoftTypography variant="body2" ml={1}>
              {status ? "Active" : "Inactive"}
            </SoftTypography>
          </SoftBox>
          )}
          {canSubmit && (
          <SoftButton
            type="submit"
            color="info"
            disabled={submitLoading}
            endIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitLoading ? "Processing..." : "Submit Branch"}
          </SoftButton>
          )}
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default AddBranch;
